import { CallClient, TeamsCallAgent } from '@azure/communication-calling';
import { AzureCommunicationTokenCredential, EntraCommunicationTokenCredentialOptions } from '@azure/communication-common';
import { getMsalTokenCredential, unifyAuthScopes } from '../authentication/msalConfig';
import { getAcsToken } from '@/repository/queryFns';
import { Lockr } from "@/common/Lockr";
import { StorageKeys } from '../constants/storageKeys';
import { captureError } from '../helpers/sentryHelper';
import { AcsClientType } from '../constants/shared';
import { AppSettings } from '@/repository/models/app';
import { AcsTokenError, EntraTokenCredentialError, MsalAuthError } from './error';
import { isTokenExpired } from '../authentication/jwtDecoder';

let incomingCallAgent: TeamsCallAgent | null = null;
let outgoingCallAgent: TeamsCallAgent | null = null;

const createCallAgent = async (credential: AzureCommunicationTokenCredential): Promise<TeamsCallAgent> => {
    const callClient = new CallClient();

    return callClient.createTeamsCallAgent(credential);
};

const getAgentWithBackendToken = async (): Promise<TeamsCallAgent | null> => {
    try {
        // Try to reuse stored token
        let token = Lockr.get(StorageKeys.TokenAcs) as string | null;

        if (!token || isTokenExpired(token)) {
            // If missing/expired, fetch new from backend
            token = await getAcsToken();
            Lockr.set(StorageKeys.TokenAcs, token);
        }

        // Always create agent with the valid token
        const credential = new AzureCommunicationTokenCredential({
            tokenRefresher: getAcsToken,
            refreshProactively: true,
            token
        });

        return await createCallAgent(credential);
    } catch (error: any) {
        if (error instanceof MsalAuthError) {
            throw error;
        }

        throw new AcsTokenError(
            "Failed to exchange ACS token or create agent with backend token."
        );
    }
};


const getAgentWithEntraToken = async (appSettings: AppSettings): Promise<TeamsCallAgent | null> => {
    try {
        const tokenCredential = getMsalTokenCredential();
        const entraTokenCredentialOptions: EntraCommunicationTokenCredentialOptions = {
            resourceEndpoint: appSettings.acsResourceEndpointUrl,
            tokenCredential,
            scopes: unifyAuthScopes,
        };
        const credential = new AzureCommunicationTokenCredential(entraTokenCredentialOptions);

        return await createCallAgent(credential);
    } catch (error) {
        if (error instanceof MsalAuthError) {
            throw error;
        }

        throw new EntraTokenCredentialError("User may not have proper Azure permissions or resource access.");
    }
};

const tryInitializeAgents = async (
    initializeFn: () => Promise<void>,
    retryMessage: string
): Promise<void> => {
    try {
        await initializeFn();
    } catch (error: any) {
        captureError(error, retryMessage + " - Retrying after cleanup.");

        Lockr.rm(StorageKeys.TokenAcs); // Remove potentially bad token

        try {
            await initializeFn(); // Retry once
        } catch (retryError: any) {
            captureError(retryError, retryMessage + " - Retry failed.");

            throw retryError;
        }
    }
};

const initializeTeamsAndCCaaSAgents = async (appSettings: AppSettings): Promise<void> => {
    await tryInitializeAgents(async () => {
        incomingCallAgent = await getAgentWithBackendToken();
        outgoingCallAgent = await getAgentWithEntraToken(appSettings);
    }, "initializeTeamsAndCCaaSAgents failed");
};

const initializeCCaaSAgents = async (appSettings: AppSettings): Promise<void> => {
    await tryInitializeAgents(async () => {
        incomingCallAgent = await getAgentWithEntraToken(appSettings);
        outgoingCallAgent = incomingCallAgent;
    }, "initializeCCaaSAgents failed");
};

export const initializeCallAgent = async (appSettings: AppSettings): Promise<void> => {
    switch (appSettings.acsClientType) {
        case AcsClientType.TeamsAndCCaaS:
            await initializeTeamsAndCCaaSAgents(appSettings);
            break;
        case AcsClientType.Teams:
        case AcsClientType.CCaaS:
            await initializeCCaaSAgents(appSettings);
            break;
        default:
            throw new Error("Unsupported ACS client type");
    }
};

export const getIncomingCallAgent = (): TeamsCallAgent | null => incomingCallAgent;

export const getOutgoingCallAgent = (): TeamsCallAgent | null => outgoingCallAgent;